/**
 * «Ekranlar devori» — what every kiosk is DISPLAYING, on one page.
 *
 * ============================================================================
 * THE TILES ARE PORTRAIT BECAUSE THE KIOSKS ARE.
 * ============================================================================
 *
 * A Navoiy kiosk is a 1080×1920 panel stood on its end. Drawing its screen into
 * a 16:9 tile — which is what every dashboard grid defaults to — would either
 * letterbox it into a third of the tile or crop off the top and bottom, and the
 * top of a kiosk screen is where the announcement takeover lives. So the tile is
 * 9:16 and the grid packs many narrow columns, which is also how a wall of
 * phone screens naturally reads.
 *
 * ============================================================================
 * A TILE THAT IS NOT SHOWING A PICTURE MUST SAY WHY. FOUR REASONS, NOT ONE.
 * ============================================================================
 *
 *   the kiosk is OFFLINE        → send a technician
 *   a citizen is in MUROJAAT    → deliberate; the machine is working perfectly
 *   the first frame is COMING   → the wall opened a second ago; wait
 *   the frames went STALE       → it was mirroring and stopped
 *
 * Only the first and last are faults. The «coming» state is specific to this
 * screen and does not exist on the camera wall: a camera runs continuously once
 * enabled, whereas a kiosk mirrors ONLY while somebody is watching — so for the
 * first few seconds after this page opens, every tile legitimately has no
 * picture. Without a word for that state, opening the wall would look like a
 * fleet-wide failure that fixes itself.
 *
 * ============================================================================
 * WATCHING IS THE ONLY SWITCH, AND CLOSING THE TAB TURNS IT OFF.
 * ============================================================================
 *
 * There is no enable button anywhere on this screen, because there is no
 * persistent setting behind it. This page renews a fifteen-second window while
 * it is mounted; the cleanup deliberately does NOTHING, so a crashed tab, a
 * closed laptop and an operator who navigated away all stop the fleet
 * identically. A "stop mirroring" call would be a promise this screen cannot
 * keep.
 */
import { useCallback, useEffect, useState } from 'react';
import type { components } from '@navoiy/api-types';
import { compareKioskVersions, KIOSK_SCREEN_MIRROR_MIN_VERSION } from '@navoiy/domain';
import { api, ApiError, apiBaseUrl, unwrap } from '../api/client.js';
import { useSession } from '../auth/SessionContext.js';
import { hasKey, t } from '../i18n/index.js';
import { ageLabel } from '../lib/format.js';
import { EmptyState, Notice, Panel, PanelHeader, Skeleton, StatusPill } from '../ui/index.jsx';
import { KpiCard, PageHeader } from '../ui/console.jsx';
import { LiveBadge } from '../ui/data.jsx';
import { NoSignalLayer, SearchingSignal } from '../ui/nosignal.jsx';
import { IconClose } from '../ui/icons.jsx';

type MbtScreenTile = components['schemas']['MbtScreenTile'];

/** How often each tile re-fetches its picture. Matches the kiosk's grid rate. */
const FRAME_MS = 3_000;

/** The rate while ONE kiosk is open full-size. Matches the server's live rate. */
const LIVE_FRAME_MS = 700;

/** How often the tile METADATA (status, screen name, queue) reloads. */
const META_MS = 5_000;

/**
 * How often to tell the server this wall is open.
 *
 * The server's window is 15 s, so renewing every 5 s survives two lost
 * requests — and letting it lapse is how watching stops.
 */
const WATCH_MS = 5_000;

/**
 * A frame older than this is not the present.
 *
 * Three grid intervals. One missed frame is a dropped packet; three in a row is
 * a kiosk that has stopped mirroring, and showing its last screenshot past that
 * point tells an operator a machine is on the attract screen when nobody knows.
 */
const STALE_MS = 10_000;

/**
 * How long a tile may have no picture before that counts as a fault.
 *
 * A kiosk polls for watchers every five seconds and then captures, so the first
 * frame after this page opens can honestly take eight. Until then a tile says
 * «kutilmoqda» rather than «eskirgan» — calling a kiosk stale for obeying the
 * protocol is how an operator learns to distrust the wall.
 */
const WARMUP_MS = 12_000;

/** A route name the panel has a word for; otherwise the raw name is shown. */
function screenLabel(screen: string | undefined): string | null {
  if (!screen) return null;
  /* `routes.` and not `route.`: `t()` splits its key on dots, so a
     `screenwall.route` string and a `screenwall.route.*` namespace cannot both
     exist — one silently wins and the other returns its own key forever. */
  return hasKey(`screenwall.routes.${screen}`) ? t(`screenwall.routes.${screen}`) : screen;
}

export function ScreenWallScreen(): React.JSX.Element {
  const { expire } = useSession();
  const onExpired = useCallback(() => expire(), [expire]);

  const [items, setItems] = useState<MbtScreenTile[] | null>(null);
  const [error, setError] = useState<'forbidden' | 'network' | null>(null);
  const [now, setNow] = useState(() => Date.now());
  /** Bumped on every refresh; appended to each `<img>` URL to defeat caching. */
  const [tick, setTick] = useState(0);
  /** The kiosk opened full-size, if any. */
  const [focused, setFocused] = useState<string | null>(null);
  /** When this wall opened — the clock the «warming up» state is measured from. */
  const [openedAt] = useState(() => Date.now());

  const load = useCallback(async () => {
    try {
      const data = unwrap(await api().GET('/mbt/fleet/screens', {}));
      setItems(data.items as MbtScreenTile[]);
      setError(null);
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 401) onExpired();
      else if (caught instanceof ApiError && caught.status === 403) setError('forbidden');
      else setError('network');
    }
  }, [onExpired]);

  useEffect(() => {
    void load();
    const timer = setInterval(() => {
      setNow(Date.now());
      void load();
    }, META_MS);
    return () => clearInterval(timer);
  }, [load]);

  /**
   * The pictures. Separated from the metadata poll so a frame refresh does not
   * drag a database query behind it — and it runs FASTER while one kiosk is
   * open full-size, matching the rate that kiosk was asked to capture at.
   */
  useEffect(() => {
    const ms = focused === null ? FRAME_MS : LIVE_FRAME_MS;
    const timer = setInterval(() => setTick((previous) => previous + 1), ms);
    return () => clearInterval(timer);
  }, [focused]);

  /**
   * Hold the watch window open.
   *
   * `live` is true only when ONE kiosk is enlarged, and then only that kiosk is
   * named — so opening a single screen full-size does not put thirty-four other
   * machines into a faster capture loop for a picture nobody is looking at.
   */
  useEffect(() => {
    const all = (items ?? []).map((tile) => tile.device_id);
    const ids = focused === null ? all : [focused];
    if (ids.length === 0) return undefined;
    const renew = () => {
      void api()
        .POST('/mbt/fleet/screens/watch', {
          body: { device_ids: ids, ...(focused === null ? {} : { live: true }) },
        })
        .catch(() => {
          // A missed renewal costs one lapsed window and the next tick fixes
          // it. Never surfaced — an operator does not need to be told about a
          // dropped keep-alive.
        });
    };
    renew();
    const timer = setInterval(renew, WATCH_MS);
    return () => clearInterval(timer);
  }, [items, focused]);

  /** Escape closes the enlarged view — the key everybody presses first. */
  useEffect(() => {
    if (focused === null) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setFocused(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [focused]);

  if (error === 'forbidden') {
    return <Notice tone="crit">{t('screenwall.noPermission')}</Notice>;
  }

  const total = items?.length ?? 0;
  const mirroring =
    items?.filter(
      (tile) =>
        tile.online &&
        !tile.appeal_in_progress &&
        tile.frame_at !== undefined &&
        now - new Date(tile.frame_at).getTime() <= STALE_MS,
    ).length ?? 0;
  const inMurojaat = items?.filter((tile) => tile.appeal_in_progress).length ?? 0;
  const offline = items?.filter((tile) => !tile.online).length ?? 0;
  /* «Stalled» EXCLUDES kiosks whose build predates the feature — counting them
     would put a number in an amber card that no technician visit could ever
     bring down. They have their own count below. */
  const tooOld = (tile: MbtScreenTile): boolean =>
    tile.app_version !== undefined &&
    compareKioskVersions(tile.app_version, KIOSK_SCREEN_MIRROR_MIN_VERSION) < 0;
  const stalled =
    items?.filter(
      (tile) =>
        tile.online &&
        !tile.appeal_in_progress &&
        !tooOld(tile) &&
        now - openedAt > WARMUP_MS &&
        (tile.frame_at === undefined || now - new Date(tile.frame_at).getTime() > STALE_MS),
    ).length ?? 0;
  const needUpdate = items?.filter((tile) => tile.online && tooOld(tile)).length ?? 0;

  /**
   * The newest build anywhere in the fleet, by simple version ordering.
   *
   * `localeCompare` with `numeric` so 0.6.10 sorts ABOVE 0.6.9 — a plain string
   * compare puts «0.6.9» last and would mark the whole fleet outdated the day
   * a tenth patch ships, which is exactly when nobody would believe the wall.
   */
  const versions = [...new Set((items ?? []).map((tile) => tile.app_version).filter(Boolean))] as string[];
  const newest =
    versions.length === 0
      ? null
      : versions.sort((a, b) => compareKioskVersions(b, a))[0] ?? null;
  const behind = (items ?? []).filter(
    (tile) => newest !== null && tile.app_version !== undefined && tile.app_version !== newest,
  ).length;

  const focusedTile = items?.find((tile) => tile.device_id === focused) ?? null;

  return (
    <div className="space-y-4">
      <PageHeader
        eyebrow={
          <>
            <LiveBadge label={t('status.live')} />
            <span>{t('screenwall.eyebrow')}</span>
          </>
        }
        title={t('nav.screens')}
        subtitle={t('screenwall.subtitle', { live: mirroring, total })}
        right={
          <p className="text-hero font-bold tabular-nums leading-none text-ink-100">{mirroring}</p>
        }
      />

      {error === 'network' ? <Notice tone="warn">{t('error.network')}</Notice> : null}

      {/*
        The four tile states as counters, faults LAST.

        «Murojaat» leads the non-fault pair because it is the number most likely
        to be misread: a run of blank tiles at ten in the morning is citizens
        using the kiosks, which is the system working, and an operator who reads
        it as breakage will go looking for a problem that is not there.
      */}
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <KpiCard
          label={t('screenwall.kpiMirroring')}
          value={String(mirroring)}
          absent={t('app.notYet')}
          tone={mirroring > 0 ? 'ok' : 'neutral'}
          footerLabel={t('screenwall.kpiOfTotal')}
          footerValue={String(total)}
        />
        <KpiCard
          label={t('screenwall.kpiMurojaat')}
          value={String(inMurojaat)}
          absent={t('app.notYet')}
          tone="info"
          footerLabel={t('screenwall.kpiMurojaatMeaning')}
          footerValue=""
        />
        <KpiCard
          label={t('screenwall.kpiOffline')}
          value={String(offline)}
          absent={t('app.notYet')}
          tone={offline > 0 ? 'offline' : 'ok'}
          footerLabel={t('screenwall.kpiOfflineMeaning')}
          footerValue=""
        />
        <KpiCard
          label={t('screenwall.kpiStalled')}
          value={String(stalled)}
          absent={t('app.notYet')}
          tone={stalled > 0 ? 'warn' : 'ok'}
          footerLabel={t('screenwall.kpiStalledMeaning')}
          footerValue=""
        />
      </div>

      {/*
        THE VERSION LINE, right under the counters.

        Owner request (2026-08-18): show what build each kiosk is on. It lives
        here as well as on every tile because the fleet-wide answer — «all four
        agree on 0.6.10» — is a different fact from any one tile's, and it is
        the one an operator wants before they start reading pictures.
      */}
      {newest === null ? null : (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-card border border-hairline bg-panel px-4 py-2 shadow-card">
          <span className="text-micro uppercase tracking-wide text-ink-500">
            {t('screenwall.newest')}
          </span>
          <span className="text-label font-semibold tabular-nums text-ink-100">{newest}</span>
          <span className={`text-micro tabular-nums ${behind > 0 ? 'text-warn' : 'text-ok'}`}>
            {behind > 0 ? t('screenwall.behind', { n: String(behind) }) : t('screenwall.allCurrent')}
          </span>
          {/* The actionable half. «One kiosk is behind» is a fact; «one online
              kiosk is too old to mirror at all» is a job. */}
          {needUpdate > 0 ? (
            <span className="text-micro tabular-nums text-info">
              {t('screenwall.needUpdateCount', {
                n: String(needUpdate),
                version: KIOSK_SCREEN_MIRROR_MIN_VERSION,
              })}
            </span>
          ) : null}
        </div>
      )}

      <Notice tone="quiet">{t('screenwall.privacyNote')}</Notice>

      <Panel>
        <PanelHeader title={t('nav.screens')} aside={t('screenwall.hint')} />
        <div className="px-4 py-4">
          {items === null ? (
            <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-5 2xl:grid-cols-6">
              {[0, 1, 2, 3, 4, 5].map((key) => (
                <Skeleton key={key} className="aspect-[9/16]" />
              ))}
            </div>
          ) : items.length === 0 ? (
            <EmptyState message={t('screenwall.empty')} />
          ) : (
            /* Narrow columns, because the tiles are portrait. Six across on a
               wall display fits a whole city's fleet without scrolling. */
            <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-5 2xl:grid-cols-6">
              {items.map((tile) => (
                <ScreenTile
                  key={tile.device_id}
                  tile={tile}
                  now={now}
                  tick={tick}
                  warming={now - openedAt <= WARMUP_MS}
                  newest={newest}
                  onOpen={() => setFocused(tile.device_id)}
                />
              ))}
            </div>
          )}
        </div>
      </Panel>

      {focusedTile === null ? null : (
        <FocusedScreen
          tile={focusedTile}
          now={now}
          tick={tick}
          onClose={() => setFocused(null)}
        />
      )}
    </div>
  );
}

/**
 * Why a tile has no picture, or `null` when it has one.
 *
 * Shared by the tile and the enlarged view so the two can never disagree about
 * whether a kiosk is mirroring — which they would, eventually, if each derived
 * it from the same four fields separately.
 */
function darkReason(
  tile: MbtScreenTile,
  now: number,
  warming: boolean,
): { key: string; label: string; tone: string } | null {
  const fresh =
    tile.frame_at !== undefined && now - new Date(tile.frame_at).getTime() <= STALE_MS;
  // ORDER MATTERS: the reasons that are NOT faults come first, so nobody is
  // dispatched to a kiosk that is behaving exactly as designed.
  if (tile.appeal_in_progress) {
    return { key: 'appeal', label: t('screenwall.murojaatPrivacy'), tone: 'text-brand' };
  }
  if (!tile.online) {
    return { key: 'offline', label: t('screenwall.offline'), tone: 'text-offline' };
  }
  if (fresh) return null;
  /*
    ========================================================================
    «THE BUILD CANNOT DO THIS» IS NOT «THE PICTURE WENT STALE».
    ========================================================================

    A kiosk on a build older than the mirror sends no frame because the code
    is not there. Before this branch it fell through to «tasvir eskirgan» in
    amber — a fault label on a healthy machine, pointing an operator at a
    problem that does not exist while hiding the one action that fixes the
    tile. On the day this shipped that was every kiosk in the fleet.

    Checked BEFORE the warm-up branch, because there is nothing to warm up:
    waiting twelve seconds for a frame that can never arrive just delays a
    wrong answer.
  */
  if (
    tile.app_version !== undefined &&
    compareKioskVersions(tile.app_version, KIOSK_SCREEN_MIRROR_MIN_VERSION) < 0
  ) {
    return {
      key: 'old',
      label: t('screenwall.needsUpdate', { version: KIOSK_SCREEN_MIRROR_MIN_VERSION }),
      tone: 'text-info',
    };
  }
  if (warming) {
    return { key: 'warming', label: t('screenwall.warming'), tone: 'text-ink-500' };
  }
  return { key: 'stale', label: t('screenwall.stale'), tone: 'text-warn' };
}

/**
 * Is this dark state a LOST SIGNAL, or a deliberate one?
 *
 * ============================================================================
 * ONLY THE FIRST GETS SNOW, AND THE DISTINCTION IS THE WHOLE POINT.
 * ============================================================================
 *
 * TV static means «there is supposed to be a picture here and there is not».
 * That is exactly true of a camera somebody switched off, a kiosk that is
 * unreachable, and a feed that stopped — and it is exactly FALSE of a tile that
 * is dark because a citizen is filing a murojaat. Putting snow on that one
 * would dress a privacy guarantee up as a malfunction, which is the same
 * mistake as calling it «stale»: the reasonable response to a malfunction is to
 * send somebody to the building.
 *
 * So the privacy plate stays clean navy with a padlock on it, and the snow says
 * what snow has always said.
 */
const NO_SIGNAL = new Set(['offline', 'stale']);

/**
 * The mark on a dark tile.
 *
 * Four states, four marks — drawn inline because they exist nowhere else and
 * because a mark is what carries across a room. The word underneath is what
 * makes the mark unambiguous; neither alone is enough on a wall of tiles.
 */
function ScreenGlyph({ kind }: { readonly kind: string }) {
  const common = {
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.4,
    strokeLinecap: 'round' as const,
  };
  return (
    <svg viewBox="0 0 24 24" className="relative h-8 w-8" aria-hidden="true">
      {kind === 'appeal' ? (
        /* A padlock. The one dark state that is a PROMISE rather than a fault:
           a citizen is entering their details and the wall is not looking. */
        <>
          <rect x="5" y="10.5" width="14" height="9.5" rx="2" {...common} />
          <path d="M8.5 10.5V7.5a3.5 3.5 0 017 0v3" {...common} />
        </>
      ) : kind === 'old' ? (
        /* A downward arrow into a tray: this kiosk needs the update, and that
           is an ACTION rather than a fault. */
        <>
          <path d="M12 3.5v10" {...common} />
          <path d="M8 10l4 4 4-4" {...common} />
          <path d="M4 16.5v2.5a1.5 1.5 0 001.5 1.5h13a1.5 1.5 0 001.5-1.5v-2.5" {...common} />
        </>
      ) : kind === 'warming' ? (
        /* A clock with its hand near the top — waiting, briefly, by design. */
        <>
          <circle cx="12" cy="12" r="8.5" {...common} />
          <path d="M12 7.5V12l2.5 1.5" {...common} />
        </>
      ) : (
        /* A monitor with a stroke through it: it WAS mirroring and stopped. */
        <>
          <rect x="2.5" y="4.5" width="19" height="12" rx="2" {...common} />
          <path d="M9 20h6" {...common} />
          <path d="M3 21L21 3" {...common} />
        </>
      )}
    </svg>
  );
}

function ScreenTile({
  tile,
  now,
  tick,
  warming,
  newest,
  onOpen,
}: {
  readonly tile: MbtScreenTile;
  readonly now: number;
  readonly tick: number;
  readonly warming: boolean;
  /** The newest build seen anywhere in the fleet — what «outdated» means here. */
  readonly newest: string | null;
  readonly onOpen: () => void;
}) {
  const dark = darkReason(tile, now, warming);
  const route = screenLabel(tile.screen);
  // Compared against the NEWEST version anywhere in the fleet rather than a
  // constant compiled into the panel. Nothing here has to be updated when a
  // release ships, and a fleet that is entirely on one build shows no amber at
  // all — which is the correct reading of «every kiosk agrees».
  const outdated = newest !== null && tile.app_version !== undefined && tile.app_version !== newest;

  return (
    <button
      type="button"
      onClick={onOpen}
      className="flex flex-col overflow-hidden rounded-card border border-hairline bg-panel text-left shadow-card transition-shadow duration-state hover:shadow-lift"
    >
      {/*
        9:16 — the kiosk's own aspect. A 16:9 tile would letterbox a portrait
        screen into a third of the space or crop the top, and the top of a kiosk
        screen is where an announcement takeover appears.
      */}
      <div className="relative aspect-[9/16] w-full bg-canvas">
        {dark === null ? (
          <img
            // `tick` in the query string is what makes an <img> re-fetch. The
            // API sends `no-store`, but a browser that already has the URL
            // painted will not ask again without a changed one.
            src={`${apiBaseUrl()}/mbt/fleet/screens/${tile.device_id}/frame?v=${tick}`}
            alt={t('screenwall.frameAlt', { kiosk: tile.label })}
            /* `object-contain`, NOT `object-cover`. A kiosk screenshot is a
               document, not a photograph: cropping it to fill the tile would
               cut off exactly the header or footer somebody is trying to read.
               The letterboxing this leaves is the honest presentation. */
            className="h-full w-full object-contain"
          />
        ) : (
          /*
            THE DARK PLATE — the same navy-and-gold the camera wall uses, and
            for the same reason: a tile with no picture used to be the page's
            own background with a small word floating in it, which reads as a
            component that failed to render rather than as a state the system
            is deliberately in. Two of the four reasons here are deliberate.
          */
          <div
            className={`relative flex h-full w-full flex-col items-center justify-center gap-1.5 bg-brand-deep px-2 text-center ${
              NO_SIGNAL.has(dark.key) ? 'text-white' : `bg-hero ${dark.tone}`
            }`}
          >
            {NO_SIGNAL.has(dark.key) ? (
              <NoSignalLayer />
            ) : null}
            {/* An unreachable machine is not a dead input — it is a device that
                is not answering — so it gets a signal that is visibly looking
                for one. Every other dark state keeps its still mark. */}
            {dark.key === 'offline' ? <SearchingSignal /> : <ScreenGlyph kind={dark.key} />}
            <span className="relative rounded bg-brand-deep/80 px-1.5 py-0.5 text-nano uppercase tracking-wide">
              {dark.label}
            </span>
            {/* The ROUTE, even with no picture. This is what keeps a blank tile
                informative: an operator still learns the machine is on the
                murojaat screen without seeing what was typed into it. */}
            {route === null ? null : (
              <span className="relative rounded bg-brand-deep/80 px-1.5 text-nano text-white/60">
                {route}
              </span>
            )}
          </div>
        )}

        <span className="absolute left-2 top-2 flex items-center gap-1.5 rounded bg-canvas/85 px-1.5 py-0.5 text-micro tabular-nums">
          <span className="font-semibold text-ink-300">{tile.short_code}</span>
          {/*
            THE BUILD, ON THE PICTURE ITSELF.

            An operator looking at a kiosk showing the wrong thing asks «what
            version is that» before they ask anything else, and until now the
            answer was on a different screen. Putting it in the corner of the
            frame means the version and the evidence are photographed together:
            you can point at one tile and say what it is running.

            Outdated builds are amber, so a wall of mixed versions is legible in
            one sweep rather than by reading thirty-five small numbers.
          */}
          {tile.app_version ? (
            <span className={outdated ? 'text-warn' : 'text-ink-500'}>{tile.app_version}</span>
          ) : null}
        </span>
        {dark === null ? (
          <span className="absolute right-2 top-2 flex items-center gap-1 rounded bg-canvas/85 px-1.5 py-0.5 text-micro text-ok">
            <span className="h-1.5 w-1.5 animate-live rounded-full bg-ok" />
            {t('screenwall.liveTag')}
          </span>
        ) : null}
      </div>

      <div className="flex flex-col gap-2 border-t border-hairline px-3 py-2">
        <div className="flex items-start justify-between gap-2">
          <p className="min-w-0 truncate text-label font-semibold text-ink-100">{tile.label}</p>
          <StatusPill status={tile.status} label={t(`status.${tile.status}`)} />
        </div>
        <p className="truncate text-micro text-ink-500">{tile.mahalla_name}</p>
        {/* TWO LABELLED FACTS, not two loose spans. The route answers «what is
            it doing» in words — the one field a blank tile still has — and the
            «as of» travels with the picture always, because a frame without one
            is a still image pretending to be a live feed. */}
        <div className="grid grid-cols-2 gap-2">
          <div className="min-w-0">
            <p className="truncate text-nano uppercase text-ink-500">{t('screenwall.routeLabel')}</p>
            <p className="truncate text-micro font-semibold text-ink-100">{route ?? '—'}</p>
          </div>
          <div className="min-w-0">
            <p className="truncate text-nano uppercase text-ink-500">{t('screenwall.captured')}</p>
            <p className="truncate text-micro font-semibold tabular-nums text-ink-100">
              {tile.frame_at ? ageLabel(new Date(tile.frame_at), new Date(now), t) : '—'}
            </p>
          </div>
        </div>
      </div>
    </button>
  );
}

/**
 * One kiosk, full height.
 *
 * A modal rather than a route, because it is a LOOK rather than a destination:
 * an operator opens it, watches for a few seconds and closes it, and a URL that
 * survived a reload would leave a kiosk mirroring at the live rate for whoever
 * restored that tab.
 */
function FocusedScreen({
  tile,
  now,
  tick,
  onClose,
}: {
  readonly tile: MbtScreenTile;
  readonly now: number;
  readonly tick: number;
  readonly onClose: () => void;
}) {
  const dark = darkReason(tile, now, false);
  const route = screenLabel(tile.screen);

  return (
    /*
      FULL VIEWPORT, WITH A SHEET DOWN THE RIGHT — the same arrangement the
      camera wall uses, on purpose. Two walls of the same fleet that enlarge
      differently is two things for an operator to learn.

      `object-contain` on black: a kiosk screenshot is a DOCUMENT, and cropping
      it to fill a shape removes exactly the header or footer somebody opened it
      to read.
    */
    <div
      className="fixed inset-0 z-50 flex bg-black"
      role="dialog"
      aria-modal="true"
      aria-label={t('screenwall.frameAlt', { kiosk: tile.label })}
    >
      <div className="relative min-w-0 flex-1">
        {dark === null ? (
          <img
            src={`${apiBaseUrl()}/mbt/fleet/screens/${tile.device_id}/frame?v=${tick}`}
            alt={t('screenwall.frameAlt', { kiosk: tile.label })}
            className="absolute inset-0 h-full w-full object-contain"
          />
        ) : (
          <div
            className={`absolute inset-0 flex flex-col items-center justify-center gap-3 bg-brand-deep text-center ${
              NO_SIGNAL.has(dark.key) ? 'text-white' : `bg-hero ${dark.tone}`
            }`}
          >
            {NO_SIGNAL.has(dark.key) ? <NoSignalLayer /> : null}
            {dark.key === 'offline' ? <SearchingSignal /> : <ScreenGlyph kind={dark.key} />}
            <span className="relative rounded bg-brand-deep/80 px-3 py-1 text-lead uppercase tracking-wide">
              {dark.label}
            </span>
            {route === null ? null : (
              <span className="relative text-label text-white/60">{route}</span>
            )}
          </div>
        )}

        {dark === null ? (
          <span className="absolute left-4 top-4 flex items-center gap-2 rounded bg-black/60 px-2.5 py-1 text-label font-semibold uppercase text-ok">
            <span className="h-2 w-2 animate-live rounded-full bg-ok" />
            {t('screenwall.liveTag')}
          </span>
        ) : null}

        <span className="absolute bottom-4 left-4 rounded bg-black/60 px-2.5 py-1 text-label font-semibold tabular-nums text-white">
          {tile.short_code}
        </span>
      </div>

      <aside className="flex w-80 shrink-0 flex-col gap-4 overflow-y-auto border-l border-hairline bg-panel p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="truncate text-lead font-semibold text-ink-100">{tile.label}</p>
            <p className="truncate text-micro text-ink-500">{tile.mahalla_name}</p>
          </div>
          {/* Esc still works, but at full screen there is no backdrop left to
              click, so the way out has to be visible. */}
          <button
            type="button"
            onClick={onClose}
            title={t('screenwall.close')}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded text-ink-500 transition-colors duration-state hover:bg-panel-raised hover:text-ink-100"
          >
            <IconClose className="h-5 w-5" />
          </button>
        </div>

        <StatusPill status={tile.status} label={t(`status.${tile.status}`)} />

        <dl className="space-y-2 text-label">
          <div className="flex items-baseline justify-between gap-2 border-b border-hairline pb-2">
            <dt className="text-micro text-ink-500">{t('screenwall.routeLabel')}</dt>
            <dd className="truncate text-label font-semibold text-ink-100">{route ?? '—'}</dd>
          </div>
          <div className="flex items-baseline justify-between gap-2 border-b border-hairline pb-2">
            <dt className="text-micro text-ink-500">{t('screenwall.version')}</dt>
            <dd className="text-label font-semibold tabular-nums text-ink-100">
              {tile.app_version ?? '—'}
            </dd>
          </div>
          <div className="flex items-baseline justify-between gap-2 border-b border-hairline pb-2">
            <dt className="text-micro text-ink-500">{t('camera.statQueue')}</dt>
            <dd className="text-lead font-semibold tabular-nums text-ink-100">
              {tile.queue_depth ?? 0}
            </dd>
          </div>
          <div className="flex items-baseline justify-between gap-2 border-b border-hairline pb-2">
            <dt className="text-micro text-ink-500">{t('screenwall.today')}</dt>
            <dd className="text-lead font-semibold tabular-nums text-ink-100">
              {tile.appeals_today ?? 0}
            </dd>
          </div>
          <div className="flex items-baseline justify-between gap-2">
            <dt className="text-micro text-ink-500">{t('screenwall.captured')}</dt>
            <dd className="text-label font-semibold tabular-nums text-ink-100">
              {tile.frame_at ? ageLabel(new Date(tile.frame_at), new Date(now), t) : '—'}
            </dd>
          </div>
        </dl>

        <p className="mt-auto text-micro leading-relaxed text-ink-500">
          {t('screenwall.privacyNote')}
        </p>
        <p className="text-micro text-ink-500">{t('screenwall.closeHint')}</p>
      </aside>
    </div>
  );
}
